// ./src/components/features/Connectors/ConnectorCategorySelector.tsx
import React, { useMemo, useEffect } from "react";
import { Select } from "@mantine/core";
import {
  CONNECTOR_CATEGORIES,
  CONNECTOR_CATEGORY_LABELS,
} from "@/config/connectorCategories";

export interface ConnectorCategorySelectorProps {
  /** Selected category value */
  value: string | null | undefined;

  /** Callback when selection changes */
  onChange?: (category: string | null) => void;

  /** Placeholder text */
  placeholder?: string;

  /** Whether the field is required */
  required?: boolean;

  /** Field label */
  label?: string;

  /** Field description */
  description?: string;

  /** Error message */
  error?: React.ReactNode;

  /** Whether the field is disabled */
  disabled?: boolean;

  /** Component size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const ConnectorCategorySelector: React.FC<
  ConnectorCategorySelectorProps
> = ({
  value,
  onChange,
  placeholder = "Select a category",
  required = false,
  label,
  description,
  error,
  disabled,
  size = "md",
}) => {
  // Build options from CONNECTOR_CATEGORIES
  const categoryOptions = useMemo(() => {
    return Object.values(CONNECTOR_CATEGORIES).map((category) => ({
      value: category,
      label: CONNECTOR_CATEGORY_LABELS[category] || category,
    }));
  }, []);

  // Auto-select first option if required and no value
  useEffect(() => {
    if (required && !value && categoryOptions.length > 0) {
      onChange?.(categoryOptions[0].value);
    }
  }, [required, value, categoryOptions, onChange]);

  // Handle change
  const handleChange = (selectedValue: string | null) => {
    onChange?.(selectedValue);
  };

  return (
    <Select
      label={label}
      description={description}
      placeholder={placeholder}
      data={categoryOptions}
      value={value ?? null}
      onChange={handleChange}
      required={required}
      withAsterisk={required}
      clearable={!required}
      disabled={disabled}
      error={error}
      size={size}
    />
  );
};
